/**
 * Merge-Kern der Listen-Nebenlaeufigkeit (M-1).
 *
 * Reine Funktionen, kein React, kein Firestore. Sie beantworten die Frage, die
 * ein Generationen-Zaehler NICHT beantworten kann: nicht "ist diese Antwort
 * veraltet?" (ja/nein), sondern "veraltet um GENAU DIESE Eintraege?".
 *
 * Hintergrund: Die Listen-Ansichten laden per einmaligem `getDocs` und pflegen
 * lokale (optimistische) Mutationen direkt in den React-State. Traf eine
 * Ladeantwort ein, nachdem lokal mutiert worden war, wurde sie bisher
 * KOMPLETT verworfen. Das ist harmlos, solange die Liste schon gefuellt war —
 * und ein sichtbarer Datenverlust, wenn die verworfene Antwort die ERSTLADUNG
 * war: dann bleibt nur der gerade lokal angelegte Datensatz uebrig, bei 1000+
 * Kunden bis zum Reload.
 *
 * Ab jetzt gilt: Serverantwort = Basis, das seit Ladungsstart protokollierte
 * lokale Delta = Overlay.
 *
 * Spezifikation: `architecture-list-concurrency-merge.md` §3 (Fallmatrix),
 * §5.2 (Vertraege). Der Ladepfad-Container liegt in `hooks/useListMutationLog`.
 */

/**
 * Art einer protokollierten lokalen Mutation.
 *
 * - `'create'` → **insert-or-replace**: fehlt der Datensatz in der Basis, wird
 *   er eingefuegt. Sein Fehlen ist hier der Normalfall (der Server-Snapshot ist
 *   aelter als das Anlegen) — Nicht-Einfuegen waere exakt der behobene Bug.
 * - `'update'` → **patch-if-present**: fehlt der Datensatz in der Basis, wird
 *   er NICHT eingefuegt. Interpretation: ein anderes Geraet hat ihn geloescht,
 *   ein fremdgeloeschter Datensatz darf nicht wiederauferstehen (§3.3-c).
 * - `'delete'` → **remove-if-present**: alle Eintraege mit passender ID fallen
 *   aus der Basis heraus, auch wenn der Snapshot sie noch enthaelt (§3.3-a).
 *
 * Genau deshalb sind `create` und `update` getrennte Arten und nicht beide auf
 * Upsert abgebildet.
 */
export type ListMutationKind = 'create' | 'update' | 'delete';

/** Ein einzelner Log-Eintrag. Rein clientseitig, fluechtig, nie persistiert. */
export interface ListMutation<T> {
  /** Streng monoton wachsend, 1-basiert, pro Log-Instanz. */
  readonly seq: number;
  readonly kind: ListMutationKind;
  /** Bei `'delete'` die zu entfernenden IDs; sonst leer/ignoriert. */
  readonly ids: readonly string[];
  /** Bei `'create'`/`'update'` die Nutzlast; bei `'delete'` leer. */
  readonly items: readonly T[];
}

/**
 * Fuegt `incoming` **idempotent** in `list` ein (insert-or-replace).
 *
 * Generische Fassung des historischen `upsertCustomers`; die Semantik ist
 * absichtlich Byte-fuer-Byte dieselbe, weil `upsertCustomers` als
 * Ein-Zeilen-Wrapper darauf delegiert und dessen 11 Bestandstests der
 * Regressionsschutz sind.
 *
 * Garantien:
 * - Ein vorhandener Eintrag mit derselben ID wird **an Ort und Stelle** ersetzt
 *   (Positionsstabilitaet), nur wirklich Neues wird angehaengt.
 * - `incoming` dedupliziert sich auch gegen sich selbst; bei doppelter ID
 *   gewinnt der LETZTE Eintrag.
 * - Leeres `incoming` gibt `list` unveraendert zurueck (Referenzidentitaet).
 *
 * Bekanntes, dokumentiertes Verschluckungs-Risiko: `getId` wird als
 * Map-Schluessel **verbatim** uebernommen. Liefert es zur Laufzeit fuer mehrere
 * fachlich verschiedene Eintraege denselben Wert (z. B. `''` oder — nur ueber
 * `any`-Casts erreichbar — `undefined`), verschluckt einer den anderen. Das ist
 * identisch zum bisherigen Verhalten und deshalb kein neues Risiko.
 *
 * @param list Bestandsliste (wird nicht mutiert).
 * @param incoming Einzufuegende/ersetzende Datensaetze.
 * @param getId Extrahiert die Identitaet eines Datensatzes.
 * @returns Neue Liste; bei leerem `incoming` die Eingabeliste selbst.
 */
export function upsertById<T>(
  list: readonly T[],
  incoming: readonly T[],
  getId: (item: T) => string,
): T[] {
  if (incoming.length === 0) return list as T[];

  // Map dedupliziert auch `incoming` gegen sich selbst (Insertion-Order bleibt).
  const incomingById = new Map<string, T>();
  incoming.forEach((item) => incomingById.set(getId(item), item));

  const existingIds = new Set(list.map((item) => getId(item)));
  const merged = list.map((existing) => {
    const replacement = incomingById.get(getId(existing));
    return replacement === undefined ? existing : replacement;
  });
  const appended = Array.from(incomingById.entries())
    .filter(([id]) => !existingIds.has(id))
    .map(([, item]) => item);

  return appended.length === 0 ? merged : [...merged, ...appended];
}

/**
 * Ersetzt vorhandene Eintraege durch `incoming` — **ohne** Fehlende anzuhaengen
 * (patch-if-present, §3.3-c).
 *
 * Interner Gegenpart zu {@link upsertById}: identische Positionsstabilitaet,
 * aber kein Wiedereinfuegen. Ein Datensatz, den die Serverantwort nicht (mehr)
 * kennt, gilt als fremdgeloescht.
 */
function patchById<T>(
  list: readonly T[],
  incoming: readonly T[],
  getId: (item: T) => string,
): T[] {
  if (incoming.length === 0) return list as T[];

  const incomingById = new Map<string, T>();
  incoming.forEach((item) => incomingById.set(getId(item), item));

  return list.map((existing) => {
    const replacement = incomingById.get(getId(existing));
    return replacement === undefined ? existing : replacement;
  });
}

/** Entfernt alle Eintraege, deren ID in `ids` steht (remove-if-present). */
function removeByIds<T>(
  list: readonly T[],
  ids: readonly string[],
  getId: (item: T) => string,
): T[] {
  if (ids.length === 0) return list as T[];
  const doomed = new Set(ids);
  return list.filter((item) => !doomed.has(getId(item)));
}

/**
 * Legt `mutations` ueber `base` — Server = Basis, lokales Delta = Overlay.
 *
 * Reine Funktion: die Eingaben werden nicht mutiert, das Ergebnis ist
 * deterministisch, es gibt keine Zeit-, Netz- oder React-Abhaengigkeit.
 *
 * Reihenfolge-Garantien:
 * - Die Bestandsreihenfolge von `base` bleibt erhalten; ersetzte Eintraege
 *   behalten ihre Position.
 * - Neu eingefuegte Eintraege werden in Log-Reihenfolge ANGEHAENGT.
 * - Mutationen werden strikt in `seq`-Reihenfolge angewandt: `create→update`
 *   desselben Datensatzes ergibt den geaenderten Stand, `create→delete` ergibt
 *   nichts. `mutations` wird dafuer intern sortiert, der Aufrufer muss die
 *   Reihenfolge nicht garantieren (die Eingabe selbst bleibt unangetastet).
 *
 * Randfaelle:
 * - `mutations` leer ⇒ Kopie von `base`.
 * - `base` leer ⇒ nur `'create'`-Eintraege landen im Ergebnis.
 * - `'update'` auf eine nicht vorhandene ID ⇒ No-op (KEIN Insert).
 * - `'delete'` auf eine nicht vorhandene ID ⇒ No-op.
 * - doppelte ID innerhalb `base` ⇒ `'create'`/`'update'` ersetzen jedes
 *   Vorkommen und haengen nichts an; `'delete'` entfernt jedes Vorkommen. Es
 *   entsteht in keinem Fall ein zusaetzlicher Eintrag.
 *
 * @param base Serverantwort (autoritative Basis, wird nicht mutiert).
 * @param mutations Seit Ladungsstart protokollierte lokale Mutationen.
 * @param getId Extrahiert die Identitaet eines Datensatzes.
 * @returns Neue Liste = Basis mit darueber gelegtem lokalem Delta.
 */
export function applyListMutations<T>(
  base: readonly T[],
  mutations: readonly ListMutation<T>[],
  getId: (item: T) => string,
): T[] {
  if (mutations.length === 0) return base.slice();

  // Kopie sortieren: `mutations` ist `readonly` und gehoert dem Aufrufer.
  const ordered = [...mutations].sort((a, b) => a.seq - b.seq);

  let current: readonly T[] = base;
  for (const mutation of ordered) {
    switch (mutation.kind) {
      case 'create':
        current = upsertById(current, mutation.items, getId);
        break;
      case 'update':
        current = patchById(current, mutation.items, getId);
        break;
      case 'delete':
        current = removeByIds(current, mutation.ids, getId);
        break;
      default:
        // Unbekannte Art: bewusst ignorieren statt werfen. Ein Log-Eintrag darf
        // niemals verhindern, dass die Serverantwort sichtbar wird.
        break;
    }
  }

  // `current` kann bei durchgaengigen No-ops noch `base` selbst sein.
  return current === base ? base.slice() : (current as T[]);
}
